// Setup › Books setup (Blueprint's; the books-setup prototype), its own module out of Books.tsx (BL-189):
// PageHead, then the four steps (a RowList: Step N of 4, the step's name, Done / Now / Not yet, what it
// unlocks; the one shown marked) beside the step at hand, a Section. What can't post for a role with no
// account is a Section whose state says so, with a Link to the roles; what waits to post is a waiting
// Notice, its invoices in an Area. The chart: the template's groups as RowLists until it's installed, then
// a DataTable whose names are fields that save on leaving them, each row's Remove a ConfirmDialog (one the
// server refuses for its history asks again, "Archive … instead?"). The roles: a DataTable, the account a
// field per row, the rest in an Area. Bank accounts: a RowList, where e-transfers arrive, and the fields
// to add one. The fiscal year: a SelectField, its sentence, a Notice once it's locked. Books ready: the
// Figures and Go to Banking. Under them, the old books' imports (SetupImports.tsx). Riverstone keeps
// Setup.tsx. setup-page.css lays out the lines between the parts, tokens only.
import {
  Area,
  Button,
  ConfirmDialog,
  DataTable,
  Figures,
  Link,
  Notice,
  RowList,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type DataColumn,
  type Row,
} from '@flowology/ui-web/fl';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import { RpcError } from '@flowology/client';
import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { docHref } from '../shared/doc-link.ts';
import { holdsAny } from '../shared/may.ts';
import { archiveOffered, archivedText, removedText, removeOutcome } from './accounts-model.ts';
import { IMPORT_PERMS } from './corrections-model.ts';
import { BOOKS_PARTS } from './parts.ts';
import { SetupImports } from './SetupImports.tsx';
import type { BooksPart } from './props.ts';
import {
  fiscalYearText,
  GROUPS,
  MONTHS,
  plural,
  roleKey,
  stepDone,
  STEPS,
  SUBTYPE_LABEL,
  type Account,
  type BankAccount,
  type Role,
  type Status,
  type Step,
} from './setup-model.ts';
import { errorText, useAct, useQuery } from './ui.tsx';
import './setup-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'setup')!;

type Banks = { bankAccounts: BankAccount[]; etransferBankAccountId: string | null };

/** A part still loading (skeleton rows), or one that didn't load (a red sentence and Try again). */
function Waiting({ error, what, onRetry }: { error: string | null; what: string; onRetry: () => void }) {
  return error ? (
    <p className="bp-setup-bad" role="alert">
      <span>{error}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  ) : (
    <SkeletonGroup label={`Loading ${what}`}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
}

/** The four steps, each with what it unlocks; with no setup yet, only their names. */
function stepRows(
  s: Status | null,
  bankCount: number,
  shown: Step | null,
  open: ((st: Step) => void) | null,
): Row[] {
  const done = (st: Exclude<Step, 'ready'>) => !!s && stepDone(st, s, bankCount);
  const next = s ? (STEPS.find((x) => !done(x.id))?.id ?? null) : undefined;
  const missing = s ? s.roles.filter((r) => !r.accountId).length : 0;
  const rows: Row[] = STEPS.map((st, i) => {
    // what the step says it has, under its name
    const has = (): string | null => {
      if (!s) return null;
      if (st.id === 'fy' && s.posted)
        return `${MONTHS[s.fiscalYearStartMonth - 1]} · locked after the first posting`;
      if (!done(st.id)) return null;
      if (st.id === 'chart') return plural(s.accounts, 'account');
      if (st.id === 'roles') return `${s.roles.length} roles mapped`;
      if (st.id === 'bank') return plural(bankCount, 'bank account');
      return fiscalYearText(s.fiscalYearStartMonth);
    };
    const word: Row['word'] = !s
      ? undefined
      : st.id === 'roles' && s.accounts > 0 && missing
        ? { text: `${plural(missing, 'role needs', 'roles need')} an account`, tone: 'bad' }
        : done(st.id)
          ? { text: 'Done' }
          : next === st.id
            ? { text: 'Now' }
            : { text: 'Not yet', tone: 'muted' };
    const line = has();
    return {
      id: st.id,
      kicker: `Step ${i + 1} of 4`,
      name: st.name,
      ...(open
        ? { onOpen: () => open(st.id), current: shown === st.id ? ('step' as const) : undefined }
        : {}),
      ...(word ? { word } : {}),
      lines: [...(line ? [{ text: line }] : []), { text: <>Unlocks {st.unlocks}</>, size: 'sm' as const }],
      marked: shown === st.id,
    };
  });
  if (s && next === null)
    rows.push({
      id: 'ready',
      name: 'Books ready',
      ...(open
        ? { onOpen: () => open('ready'), current: shown === 'ready' ? ('step' as const) : undefined }
        : {}),
      word: { text: 'Done' },
      lines: [{ text: 'Everything posts on its own' }],
      marked: shown === 'ready',
    });
  return rows;
}

export function BooksSetupPage({
  nav,
  held,
  toast,
  go,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  /** Books ready's Go to Banking. */
  go: (part: BooksPart) => void;
}) {
  const status = useQuery<Status>('accounts.setupStatus', {});
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', { includeInactive: false });
  const banks = useQuery<Banks>('bankAccounts.list', {});
  const reload = async () => {
    await Promise.all([status.reload(), accounts.reload(), banks.reload()]);
  };
  const crumb = usePageCrumb(nav);
  const s = status.data;
  const bankCount = banks.data?.bankAccounts.length ?? 0;
  const next = s ? (STEPS.find((x) => !stepDone(x.id, s, bankCount))?.id ?? null) : null;
  const [step, setStep] = useState<Step | null>(null);
  // open on the step that's next (or Books ready), once the setup has loaded
  useEffect(() => {
    if (step === null && s && banks.data) setStep(next ?? 'ready');
  }, [s, banks.data, step, next]);
  const ready = !!(s && banks.data && accounts.data && step);
  const canEdit = s?.canEdit ?? false;
  const missing = s ? s.roles.filter((r) => !r.accountId) : [];
  const blocker = missing.find((r) => r.role === 'accounts_receivable') ?? missing[0];
  const props = { s: s!, canEdit, toast, reload, setStep };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <PageHead {...(crumb ? { crumb } : {})} title={PART.bp ?? PART.rail} lede={PART.lede} />
        <div className="bp-setup">
          {ready && !canEdit && (
            <Notice tone="waiting" title="The owner or bookkeeper sets this up.">
              You can see the setup, not change it.
            </Notice>
          )}
          {ready && s!.accounts > 0 && blocker && (
            <Section title={blocker.blocks} state={{ mark: 'problem', word: 'Needs an account' }}>
              <p className="bp-setup-line">
                Until {blocker.label} ({blocker.tech}) has an account. Pick an account in{' '}
                <Link onPress={() => setStep('roles')}>Account roles</Link> and save.
              </p>
            </Section>
          )}
          {ready && s!.waiting.total > 0 && <WaitingToPost waiting={s!.waiting} />}
          <div className="bp-setup-steps">
            <nav className="bp-setup-river" aria-label="What’s left to do">
              <RowList
                label="The four steps"
                rows={stepRows(ready ? s : null, bankCount, ready ? step : null, ready ? setStep : null)}
              />
            </nav>
            <div className="bp-setup-step">
              {!ready ? (
                <Section title="Books setup">
                  <Waiting
                    error={status.error ?? banks.error ?? accounts.error}
                    what="the books setup"
                    onRetry={() => void reload()}
                  />
                </Section>
              ) : step === 'chart' ? (
                <ChartStep {...props} accounts={accounts.data!.accounts} />
              ) : step === 'roles' ? (
                <RolesStep {...props} accounts={accounts.data!.accounts} />
              ) : step === 'bank' ? (
                <BankStep
                  {...props}
                  accounts={accounts.data!.accounts}
                  banks={banks.data!.bankAccounts}
                  etransferId={banks.data!.etransferBankAccountId}
                />
              ) : step === 'fy' ? (
                <FiscalYearStep {...props} />
              ) : (
                <Section title="The books are ready">
                  <p className="bp-setup-say">
                    Invoices, payments and bills now post on their own. Here’s what you set up.
                  </p>
                  <Figures
                    label="What you set up"
                    items={[
                      { label: 'accounts', value: s!.accounts },
                      { label: 'roles mapped', value: s!.roles.length },
                      { label: bankCount === 1 ? 'bank account' : 'bank accounts', value: bankCount },
                      { label: 'year starts', value: MONTHS[s!.fiscalYearStartMonth - 1]!.slice(0, 3) },
                    ]}
                  />
                  <div className="bp-setup-acts">
                    <Button variant="primary" onPress={() => go('banking')}>
                      Go to Banking
                    </Button>
                  </div>
                  <p className="bp-setup-small">You can come back here any time from Books › Setup.</p>
                </Section>
              )}
            </div>
          </div>
          {/* gap #17: the old chart, opening balances and history summaries, once the setup has loaded */}
          {ready && holdsAny(held, IMPORT_PERMS) && (
            <SetupImports toast={toast} canChart={canEdit} onChart={reload} />
          )}
        </div>
      </main>
    </div>
  );
}

/** What waits to post for a role with no account: a waiting Notice, the invoices it holds in an Area under it. */
function WaitingToPost({ waiting: w }: { waiting: Status['waiting'] }) {
  const rest = w.total - w.invoices.length;
  return (
    <div className="bp-setup-waiting">
      <Notice
        tone="waiting"
        title={
          w.invoiceCount > 0
            ? `${plural(w.invoiceCount, 'invoice is', 'invoices are')} waiting to post.`
            : `${plural(w.total, 'posting is', 'postings are')} waiting to post.`
        }
      >
        They post on their own once the role has an account.
      </Notice>
      {w.invoices.length > 0 && (
        <Area title="See the waiting postings" level={3}>
          <RowList
            label="The waiting postings"
            rows={w.invoices.map((i) => ({
              id: i.id,
              name: (
                <>
                  Invoice <Link href={docHref({ kind: 'invoice', id: i.id })}>{i.number}</Link>
                </>
              ),
            }))}
            {...(rest > 0 ? { note: `and ${plural(rest, 'other posting')}` } : {})}
          />
        </Area>
      )}
    </div>
  );
}

interface StepProps {
  s: Status;
  canEdit: boolean;
  toast: (m: string) => void;
  reload: () => Promise<void>;
  setStep: (s: Step) => void;
}

function ChartStep({ s, canEdit, toast, reload, setStep, accounts }: StepProps & { accounts: Account[] }) {
  const act = useAct(toast, reload);
  const [names, setNames] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState({ code: '', name: '', subtype: 'expense' });
  const [removing, setRemoving] = useState<Account | null>(null);
  const [archiving, setArchiving] = useState<{ account: Account; why: string } | null>(null);
  if (s.accounts === 0)
    return (
      <Section title="Start from the Canada template for home-service trades">
        <p className="bp-setup-say">
          {s.template.length} accounts a plumbing, heating or electrical company uses, with every posting role
          already pointed at the right one. You can rename and add accounts after.
        </p>
        <div className="bp-setup-groups">
          {GROUPS.map(([label, subtypes]) => (
            <RowList
              key={label}
              title={label}
              rows={s.template
                .filter((a) => subtypes.includes(a.subtype))
                .map((a) => ({ id: a.code, kicker: a.code, name: a.name }))}
            />
          ))}
        </div>
        {canEdit && (
          <div className="bp-setup-acts">
            <Button
              variant="primary"
              isDisabled={act.busy}
              onPress={() =>
                void act
                  .run('accounts.installTemplate', { country: 'CA' }, 'The template is installed.')
                  .then((r) => {
                    if (r) setStep('roles');
                  })
              }
            >
              {act.busy ? 'Installing…' : 'Install the template'}
            </Button>
          </div>
        )}
      </Section>
    );
  const rename = (a: Account) => {
    const name = (names[a.id] ?? a.name).trim();
    if (!name || name === a.name) return;
    void act.run(
      'accounts.update',
      { id: a.id, name, rowVersion: a.rowVersion },
      `${a.code} is now ${name}.`,
    );
  };
  const remove = async () => {
    const a = removing;
    if (!a) return;
    try {
      await client.call('accounts.remove', { id: a.id });
    } catch (e) {
      // one with history is archived instead: the question asked again, the server's words with it
      if (e instanceof RpcError && archiveOffered(e)) {
        setArchiving({ account: a, why: errorText(e) });
        return;
      }
      throw new Error(errorText(e), { cause: e });
    }
    toast(removedText(a));
    await reload();
  };
  const archive = async () => {
    const a = archiving?.account;
    if (!a) return;
    try {
      await client.call('accounts.update', { id: a.id, active: false, rowVersion: a.rowVersion });
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    toast(archivedText(a));
    await reload();
  };
  const columns: DataColumn<Account>[] = [
    { id: 'code', label: 'Code', rowHeader: true, cell: (a) => ({ text: a.code }) },
    {
      id: 'name',
      label: 'Name',
      ...(canEdit ? { kind: 'control' as const } : {}),
      cell: (a) =>
        canEdit
          ? {
              control: {
                label: `Name of account ${a.code}`,
                value: names[a.id] ?? a.name,
                onChange: (v) => setNames({ ...names, [a.id]: v }),
                // leaving the field saves it (Enter leaves it): one save, never two with the same version
                onBlur: () => rename(a),
              },
            }
          : { text: a.name },
    },
    { id: 'type', label: 'Type', cell: (a) => ({ text: SUBTYPE_LABEL[a.subtype] ?? a.subtype }) },
    ...(canEdit
      ? [
          {
            id: 'remove',
            label: 'Remove',
            kind: 'action' as const,
            cell: (a: Account) => ({
              action: {
                text: 'Remove',
                label: `Remove account ${a.code} ${a.name}`,
                onPress: () => setRemoving(a),
              },
            }),
          },
        ]
      : []),
  ];
  return (
    <Section title="Chart of accounts">
      <p className="bp-setup-say">
        {plural(accounts.length, 'account')}. Rename any of them; codes are a convention, not meaning.
        {s.posted
          ? ' After the first posting an account’s type can’t change, but renaming is still fine.'
          : ''}
      </p>
      <DataTable label="Chart of accounts" columns={columns} rows={accounts} empty="No accounts yet." />
      {canEdit && (
        <form
          className="bp-setup-form"
          aria-label="Add an account"
          onSubmit={(e) => {
            e.preventDefault();
            void act
              .run(
                'accounts.create',
                { code: draft.code.trim(), name: draft.name.trim(), subtype: draft.subtype },
                `${draft.code.trim()} ${draft.name.trim()} is added.`,
              )
              .then((r) => {
                if (r) setDraft({ code: '', name: '', subtype: draft.subtype });
              });
          }}
        >
          <TextField
            label="Code"
            inputMode="numeric"
            placeholder="1020"
            value={draft.code}
            onChange={(code) => setDraft({ ...draft, code })}
          />
          <TextField
            label="Name"
            placeholder="Bank: savings"
            value={draft.name}
            onChange={(name) => setDraft({ ...draft, name })}
          />
          <SelectField
            label="Type"
            value={draft.subtype}
            onChange={(subtype) => setDraft({ ...draft, subtype })}
            options={Object.entries(SUBTYPE_LABEL).map(([value, label]) => ({ value, label }))}
          />
          <div className="bp-setup-acts">
            <Button type="submit" isDisabled={act.busy || !draft.code.trim() || !draft.name.trim()}>
              Add account
            </Button>
          </div>
        </form>
      )}
      <div className="bp-setup-acts">
        <Button variant="primary" onPress={() => setStep('roles')}>
          Continue to account roles
        </Button>
      </div>
      <ConfirmDialog
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.code} ${removing.name}?` : 'Remove'}
        confirmLabel="Remove it"
        onConfirm={remove}
      >
        {removing ? removeOutcome(removing) : null}
      </ConfirmDialog>
      <ConfirmDialog
        isOpen={!!archiving}
        onClose={() => setArchiving(null)}
        title={archiving ? `Archive ${archiving.account.code} ${archiving.account.name} instead?` : 'Archive'}
        confirmLabel="Archive it instead"
        onConfirm={archive}
      >
        {archiving ? archiving.why : null}
      </ConfirmDialog>
    </Section>
  );
}

function RolesStep({ s, canEdit, toast, reload, accounts }: StepProps & { accounts: Account[] }) {
  const act = useAct(toast, reload);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  if (s.accounts === 0)
    return (
      <Section title="Account roles">
        <p className="bp-setup-say">Install or import a chart first; the roles point at its accounts.</p>
      </Section>
    );
  const value = (r: Role) => picks[roleKey(r)] ?? r.accountId ?? '';
  const columns: DataColumn<Role & { id: string }>[] = [
    {
      id: 'role',
      label: 'Role',
      rowHeader: true,
      cell: (r) => ({
        text: r.label,
        lines: [{ text: r.tech }, ...(value(r) ? [] : [{ word: 'Needs an account', tone: 'bad' as const }])],
      }),
    },
    {
      id: 'account',
      label: 'Account',
      kind: 'control',
      cell: (r) => ({
        control: {
          label: `Account for ${r.label}`,
          value: value(r),
          onChange: (v) => setPicks({ ...picks, [roleKey(r)]: v }),
          options: [
            { value: '', label: 'Choose an account…' },
            ...accounts
              .filter((a) => !r.types.length || r.types.includes(a.type))
              .map((a) => ({ value: a.id, label: `${a.code} ${a.name}` })),
          ],
          ...(canEdit ? {} : { isDisabled: true }),
        },
        ...(errs[roleKey(r)] ? { lines: [{ text: errs[roleKey(r)]!, tone: 'bad' as const }] } : {}),
      }),
    },
  ];
  const changed = s.roles.filter((r) => picks[roleKey(r)] && picks[roleKey(r)] !== r.accountId);
  const save = async () => {
    const failed: Record<string, string> = {};
    for (const r of changed) {
      const ok = await act.run('accounts.mapRole', {
        role: r.role,
        key: r.key,
        accountId: picks[roleKey(r)],
      });
      if (!ok) failed[roleKey(r)] = `${r.label} can’t use that account.`;
    }
    setErrs(failed);
    if (!Object.keys(failed).length) {
      setPicks({});
      toast(changed.length ? 'Roles saved.' : 'Nothing to save.');
    }
  };
  const rows = (list: Role[]) => list.map((r) => ({ ...r, id: roleKey(r) }));
  const main = s.roles.filter((r) => r.main);
  const rest = s.roles.filter((r) => !r.main);
  const restMissing = rest.filter((r) => !value(r)).length;
  return (
    <Section title="Account roles">
      <p className="bp-setup-say">
        The accounts the posting rules use. The template pointed each one at the usual account; check the ones
        you care about.
      </p>
      <DataTable label="Account roles" columns={columns} rows={rows(main)} empty="No roles." />
      <Area
        title={`${rest.length} more roles`}
        summary={restMissing ? `${restMissing} need an account` : 'all set'}
        defaultOpen={restMissing > 0}
        level={3}
      >
        <DataTable label="More account roles" columns={columns} rows={rows(rest)} empty="No more roles." />
      </Area>
      <p className="bp-setup-small">
        Sales for a single department use <b>Sales</b> unless you give one its own income account later.
      </p>
      {canEdit && (
        <div className="bp-setup-acts">
          <Button variant="primary" isDisabled={act.busy} onPress={() => void save()}>
            {act.busy ? 'Saving…' : 'Save roles'}
          </Button>
        </div>
      )}
    </Section>
  );
}

function BankStep({
  s,
  canEdit,
  toast,
  reload,
  accounts,
  banks,
  etransferId,
}: StepProps & { accounts: Account[]; banks: BankAccount[]; etransferId: string | null }) {
  const act = useAct(toast, reload);
  const ledger = accounts.filter((a) => a.subtype === 'bank' || a.subtype === 'credit_card');
  const [d, setD] = useState({ name: '', institution: '', last4: '', accountId: '' });
  const [err, setErr] = useState<string | null>(null);
  if (s.accounts === 0)
    return (
      <Section title="Bank accounts">
        <p className="bp-setup-say">
          Install or import a chart first; a bank account points at a bank-type account in it, a company card
          at a credit card account.
        </p>
      </Section>
    );
  const accountId = d.accountId || ledger[0]?.id || '';
  const code = (id: string) => accounts.find((a) => a.id === id)?.code ?? '';
  const add = () => {
    if (!d.name.trim()) return setErr('Give the account a name.');
    if (d.last4 && !/^\d{4}$/.test(d.last4)) return setErr('The last 4 digits are four digits, like 4821.');
    if (!accountId) return setErr('Add a bank or credit card account to the chart first.');
    setErr(null);
    void act
      .run(
        'bankAccounts.create',
        {
          name: d.name.trim(),
          accountId,
          ...(d.institution.trim() ? { institution: d.institution.trim() } : {}),
          ...(d.last4 ? { last4: d.last4 } : {}),
        },
        `${d.name.trim()} is added.`,
      )
      .then((r) => {
        if (r) setD({ name: '', institution: '', last4: '', accountId: '' });
      });
  };
  const ending = (b: BankAccount) => (b.last4 ? ` · ending ${b.last4}` : '');
  return (
    <Section title="Bank accounts">
      <p className="bp-setup-say">
        The accounts your money moves through. Statements are imported against each one and reconciled to it.
      </p>
      <RowList
        label="Bank accounts"
        empty="No bank accounts yet."
        rows={banks.map((b) => ({
          id: b.id,
          name: b.name,
          after: { text: ` · ${b.institution ?? 'No bank named'}${ending(b)}` },
          lines: [{ text: `Ledger ${code(b.accountId)} · statements by CSV or OFX` }],
        }))}
      />
      {banks.length > 0 &&
        // where customer e-transfers post (payments.etransferBankAccountId): the first added until one is chosen
        (canEdit ? (
          <SelectField
            label="E-transfers arrive in"
            value={etransferId ?? banks[0]!.id}
            isDisabled={act.busy}
            onChange={(id) =>
              void act.run(
                'bankAccounts.setEtransferAccount',
                { bankAccountId: id },
                `E-transfers now arrive in ${banks.find((b) => b.id === id)?.name ?? 'that account'}.`,
              )
            }
            options={banks.map((b) => ({ value: b.id, label: `${b.name}${ending(b)}` }))}
          />
        ) : (
          <p className="bp-setup-line">
            E-transfers arrive in <b>{banks.find((b) => b.id === etransferId)?.name ?? banks[0]!.name}</b>
          </p>
        ))}
      {canEdit && (
        <form
          className="bp-setup-form"
          aria-label={banks.length ? 'Add another bank account' : 'Add a bank account'}
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <TextField
            label="Name"
            placeholder="RBC operating"
            value={d.name}
            onChange={(name) => setD({ ...d, name })}
          />
          <TextField
            label="Bank"
            placeholder="RBC Royal Bank"
            value={d.institution}
            onChange={(institution) => setD({ ...d, institution })}
          />
          <TextField
            label="Last 4 digits"
            inputMode="numeric"
            maxLength={4}
            placeholder="4821"
            value={d.last4}
            onChange={(last4) => setD({ ...d, last4 })}
          />
          <div className="bp-setup-wide">
            <SelectField
              label="Ledger account"
              description="Only bank and credit card accounts. Need another? Add one in the chart."
              value={accountId}
              onChange={(id) => setD({ ...d, accountId: id })}
              options={ledger.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))}
            />
          </div>
          {err && (
            <p className="bp-setup-bad bp-setup-wide" role="alert">
              {err}
            </p>
          )}
          <div className="bp-setup-acts">
            <Button variant="primary" type="submit" isDisabled={act.busy}>
              {act.busy ? 'Adding…' : 'Add bank account'}
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

function FiscalYearStep({ s, canEdit, toast, reload }: StepProps) {
  const act = useAct(toast, reload);
  const [month, setMonth] = useState(s.fiscalYearStartMonth);
  const locked = s.posted;
  return (
    <Section title="Fiscal year">
      <p className="bp-setup-say">
        The month your year starts. Earnings before it move to retained earnings on the balance sheet.
      </p>
      <div className="bp-setup-fit">
        <SelectField
          label="The year starts in"
          width="fit"
          value={String(month)}
          isDisabled={locked || !canEdit}
          onChange={(m) => setMonth(Number(m))}
          options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
        />
      </div>
      <p className="bp-setup-line" data-fy="">
        Your year runs <b>{fiscalYearText(month)}</b>
      </p>
      {locked ? (
        <Notice title="Can’t change after the first posting.">
          Every report and the year-end close depend on it. Account names can still change.
        </Notice>
      ) : (
        canEdit && (
          <div className="bp-setup-acts">
            <Button
              variant="primary"
              isDisabled={act.busy}
              onPress={() =>
                void act.run(
                  'accounts.setFiscalYear',
                  { startMonth: month },
                  `The year starts in ${MONTHS[month - 1]}.`,
                )
              }
            >
              {act.busy ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )
      )}
    </Section>
  );
}
